import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Card, FormControl } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import Selector from "../../components/selector";
import { SectionHeading } from "../../components/section-heading";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import { migrateReservation } from "./api";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useKeycloakAuthz } from "../../components/keycloak-provider";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.utilities.migrateReservation",
      defaultMessage: "Migrate Reservation",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    reservationId: {
      id: "migrateReservation.input.reservationId",
      defaultMessage: "Reservation Id",
    },
    migrateExistingReservation: {
      id: "migrateReservation.button.existing",
      defaultMessage: "Migrate existing reservation",
    },
    migrateNewReservation: {
      id: "migrateReservation.button.new",
      defaultMessage: "Migrate new reservation",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  gap: 16px;
`;
const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;
const ReservationIdInput = styled(FormControl)``;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

function MigrateReservation() {
  const userPropertyTickersList = useUserPropertyTickers();
  const config = useConfig();
  const httpClient = useHttpClient(config.secureApiEndpoint);
  const keycloakAuthz = useKeycloakAuthz();

  const [reservationId, setReservationId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );
  const handleReservationIdChange = useCallback(event => setReservationId(event.target.value), [
    setReservationId,
  ]);

  useEffect(() => {
    setPropertyTickerOptions(
      userPropertyTickersList.map(option => ({ value: option, label: option }))
    );
  }, [userPropertyTickersList]);

  const handleSelectPropertyTicker = useCallback(
    selectedOption => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const handleMigrateReservation = useCallback(
    async (isExistingReservation: boolean) => {
      if (!propertyTickerOption?.value || !reservationId) {
        return;
      }
      setLoading(true);
      setError(undefined);
      setConfigSuccess(false);
      try {
        const rpt = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
        await migrateReservation({
          propertyTicker: propertyTickerOption.value,
          httpClient,
          reservationId,
          isExistingReservation,
          config: {
            headers: {
              authorization: `Bearer ${rpt}`,
            },
          }
        });
        console.log("migrateReservation DONE");
        setConfigSuccess(true);
      } catch (e) {
        setConfigSuccess(false);
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
          setError({ message: "Access denied" });
        } else {
          console.error("Error migrating reservation", e);
          setError({ message: "Error migrating reservation" });
        }
      }
      setLoading(false);
    },
    [httpClient, propertyTickerOption?.value, reservationId]
  );

  if (loading) {
    return <FullWidthLoader />;
  }
  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
          <Label>
            <FormattedMessage {...messages.reservationId} />
          </Label>
          <ReservationIdInput
            type="text"
            placeholder="MXXXXXX"
            onChange={handleReservationIdChange}
          />
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={() => handleMigrateReservation(true)}>
            <FormattedMessage {...messages.migrateExistingReservation} />
          </Button>
          <Button variant="primary" type="button" onClick={() => handleMigrateReservation(false)}>
            <FormattedMessage {...messages.migrateNewReservation} />
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(MigrateReservation);
